import { FAQ_COLOR } from '../common/faq.constants';
import { MOBILE_BOTTOM_NAV_OFFSET } from '../../config/mobileNavConfig';
import { HOMEPAGE_AI_GUIDE_MASCOT_HEIGHT, HOMEPAGE_AI_GUIDE_MASCOT_WIDTH } from './homepageAiGuide.constants';
import { MOBILE_HOMEPAGE_LAYOUT as L } from './mobileHomepageLayout';

const SECTION_GUTTER = {
  paddingLeft: {
    xs: '16px',
    sm: '16px',
    md: 'clamp(20px, 1.25vw, 40px)',
    lg: 'clamp(24px, 1.25vw, 48px)',
  },
  paddingRight: {
    xs: '16px',
    sm: '16px',
    md: 'clamp(20px, 1.25vw, 40px)',
    lg: 'clamp(24px, 1.25vw, 48px)',
  },
};

const CHAT_SIDEBAR_PADDING = { xs: 0, sm: 0, md: '340px', lg: '360px', xl: '380px' };

export const homepageRootSx = {
  minHeight: '100dvh',
  background: '#F5F5F7',
  display: 'flex',
  flexDirection: 'column',
  color: '#000',
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  '& > *': {
    maxWidth: '100%',
  },
  fontSize: {
    xs: '14px',
    sm: '14px',
    md: '16px',
    lg: '17px',
    xl: '18px',
  },
  '@media (max-width: 480px)': {
    fontSize: '13px',
  },
};

export function homepageContentSx(reserveChatSidebar) {
  return {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100dvh',
    boxSizing: 'border-box',
    p: 0,
    // Matches the fixed mobile tab bar so the main footer sits against it.
    pb: { xs: MOBILE_BOTTOM_NAV_OFFSET, sm: MOBILE_BOTTOM_NAV_OFFSET, md: 0 },
    width: '100%',
    maxWidth: '100%',
    pr: reserveChatSidebar ? CHAT_SIDEBAR_PADDING : 0,
    transition: 'padding-right 0.3s',
    position: 'relative',
    zIndex: 2,
    '& > *': {
      width: '100%',
      maxWidth: '100%',
    },
  };
}

export const homepageHeaderHostSx = {
  display: { xs: 'none', sm: 'none', md: 'contents' },
};

export const homepageMainSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minWidth: 0,
  '& > *': {
    width: '100%',
    maxWidth: '100%',
  },
};

export function homepageSearchCardSx(isHotel) {
  return {
    width: { xs: L.formCardWidth, sm: L.formCardWidth, md: '100%' },
    mx: { xs: 'auto', sm: 'auto', md: 0 },
    bgcolor: { xs: 'transparent', sm: 'transparent', md: '#F5F5F7' },
    minHeight: { xs: 0, sm: 0, md: isHotel ? '50px' : '400px' },
    flex: { xs: '0 0 auto', sm: '0 0 auto', md: 1 },
    position: 'relative',
    zIndex: { xs: 2, sm: 2, md: 'auto' },
    mt: { xs: L.formCardPullUp, sm: L.formCardPullUp, md: '-28px' },
    pt: { xs: L.formCardTopClearance, sm: L.formCardTopClearance, md: '0px' },
    borderRadius: {
      xs: `${L.formCardRadius} ${L.formCardRadius} 0 0`,
      sm: `${L.formCardRadius} ${L.formCardRadius} 0 0`,
      md: 0,
    },
    overflow: 'visible',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
    },
  };
}

export const homepageFormColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
};

export const homepageSearchSlotSx = {
  order: { xs: 1, md: 1 },
  width: '100%',
  minWidth: 0,
};

export function homepageAiGuideSx({ isHotel, isMulticity }) {
  return {
    order: { xs: 0, md: 2 },
    width: '100%',
    display: { xs: 'none', sm: 'none', md: 'flex' },
    flexDirection: 'column',
    alignItems: 'center',
    mt: {
      xs: 2,
      md: isMulticity ? '104px' : '54px',
    },
    mb: {
      xs: 1,
      md: isHotel ? '54px' : isMulticity ? 6 : 2,
    },
    ...SECTION_GUTTER,
  };
}

export const homepageAiGuideClusterSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: 'max-content',
  maxWidth: '100%',
};

export const homepageAiGuideTitleSx = {
  margin: 0,
  fontWeight: 500,
  fontSize: {
    xs: '0.8rem',
    sm: '0.8rem',
    md: '16px',
    lg: '1.1rem',
    xl: '1.2rem',
  },
  color: '#000',
  textAlign: 'center',
  letterSpacing: '0.3px',
  minHeight: '1.5em',
  opacity: 1,
  lineHeight: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  width: 'max-content',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
};

export const homepageAiGuideIconSx = {
  fontSize: 'inherit',
  color: '#000',
  flexShrink: 0,
};

export const homepageAiGuideRowSx = {
  width: 0,
  minWidth: '100%',
  maxWidth: '100%',
  justifyContent: 'flex-start',
  alignItems: 'flex-end',
};

export function homepageAiGuideInputSx() {
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    borderRadius: '25px',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(-4deg, rgba(187, 0, 255, 0.44) 0%, rgba(0, 178, 255, 0.44) 30%, rgba(108, 39, 255, 0.44) 65%, rgba(255, 0, 0, 0.44) 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'content-box, border-box',
    overflow: 'hidden',
    flex: '1 1 auto',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  };
}

export const homepageAiGuideSendSx = {
  width: '40px',
  height: '40px',
  background: '#000',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '4px',
  cursor: 'pointer',
  opacity: 1,
  '&:hover': {
    background: '#333',
  },
};

export const homepageAiGuideSendIconSx = {
  width: '24px',
  height: '24px',
  objectFit: 'contain',
};

export function homepageAiGuideMascotSx(height) {
  if (!height) {
    return { height: 0, width: 0, opacity: 0, overflow: 'hidden' };
  }
  const slotWidth = Math.round(height * HOMEPAGE_AI_GUIDE_MASCOT_WIDTH / HOMEPAGE_AI_GUIDE_MASCOT_HEIGHT);
  return {
    height: `${height}px`,
    width: `${slotWidth}px`,
    minWidth: `${slotWidth}px`,
    flex: '0 0 auto',
    opacity: 1,
    overflow: 'hidden',
  };
}

export const homepageBannerSlotSx = {
  order: { xs: 2, md: 3 },
  width: '100%',
  minWidth: 0,
};

export const homepageTestimonialsSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, #FAFBFC 0%, #F3F4F6 50%, #FAFBFC 100%)',
  mt: 2,
  py: { xs: '20px', sm: '20px', md: '40px' },
  ...SECTION_GUTTER,
};

export const homepageTestimonialsInnerSx = {
  width: '100%',
  maxWidth: {
    xs: '100%',
    sm: '100%',
    md: '1200px',
    lg: '1400px',
    xl: '1600px',
  },
  mx: 'auto',
};

export const homepageFaqSx = {
  background: FAQ_COLOR.canvas,
  pt: 0,
  pb: { xs: 6, md: 10 },
};
